import type { ReactNode } from 'react';
import { PlainLink } from '@customerio/pluma-components/react';
import { GatedFeaturesPopover } from '@/features/gated-features';

type DirectoryCardLinkProps = {
  cardType?: string;
  className?: string;
  featureGated: boolean;
  external?: boolean;
  isPipelineCard: boolean;
  /** Resolved destination href for non-gated cards. */
  href: string;
  onClick: () => void;
  gatedTitle: string;
  gatedText: string;
  featureName?: string;
  children: ReactNode;
};

/**
 * Resolves how a directory card behaves on click:
 * - feature-gated → an upgrade popover (no navigation, no click event)
 * - external / pipelines → a real anchor to the (cross-app) URL
 * - otherwise → a client-side link to the embedded connector route
 */
export function DirectoryCardLink({
  cardType,
  className,
  featureGated,
  external,
  isPipelineCard,
  href,
  onClick,
  gatedTitle,
  gatedText,
  featureName,
  children,
}: DirectoryCardLinkProps) {
  if (featureGated) {
    return (
      <GatedFeaturesPopover
        className={className}
        triggerVariant="plain"
        featureName={featureName}
        title={gatedTitle}
        text={gatedText}
        data-test-action-integration-card={cardType}
        data-test-integrations-card={cardType}
      >
        {children}
      </GatedFeaturesPopover>
    );
  }

  return (
    <PlainLink
      href={href}
      isExternal={external === true || isPipelineCard}
      withSafeExternalAttributes={isPipelineCard ? false : undefined}
      className={className}
      onClick={onClick}
      data-test-action-integration-card={cardType}
      data-test-integrations-card={cardType}
    >
      {children}
    </PlainLink>
  );
}
